import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { User, Save, Loader2, ArrowRight } from 'lucide-react';

export default function CreateUserPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    roleId: "staff-role-001",
    branchId: null,
    isActive: true
  });

  const handleChange = (field: string, value: any) => {
    // ضمان أن roleId يُرسل كنص (String) كما يتوقع الـ Backend
    if (field === 'roleId') {
      value = String(value);
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log("البيانات المرسلة للـ Backend:", JSON.stringify(form, null, 2));
      
      const accountType = form.roleId.startsWith('delegate') ? 'delegate' : form.roleId.startsWith('merchant') ? 'merchant' : 'staff';
      const payload = {
        name: form.fullName,
        email: form.email,
        password: form.password,
        phone: form.phone,
        address: form.address,
        roleId: form.roleId,
        branchId: form.branchId,
        isActive: form.isActive,
        accountType
      };
      const response = await api.post('/api/users/managed', payload);
      if (response.data.success) {
        alert('تم إنشاء المستخدم بنجاح!');
        navigate('/users');
      } else {
        alert(response.data.message || 'فشل إنشاء المستخدم');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.message || 'حدث خطأ أثناء إنشاء المستخدم');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition bg-white";
  const labelClass = "block text-xs font-bold text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50/30 p-6" dir="rtl">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
          <button onClick={() => navigate('/users')} className="p-2 hover:bg-gray-100 rounded-full transition">
            <ArrowRight className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">إضافة مستخدم جديد</h1>
              <p className="text-gray-500 text-sm">أدخل بيانات المستخدم الجديد</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>الاسم الكامل *</label>
              <input required type="text" value={form.fullName} onChange={e => handleChange('fullName', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>اسم المستخدم *</label>
              <input required type="text" value={form.username} onChange={e => handleChange('username', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>البريد الإلكتروني *</label>
              <input required type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>كلمة المرور *</label>
              <input required type="password" value={form.password} onChange={e => handleChange('password', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>رقم الهاتف</label>
              <input type="tel" value={form.phone} onChange={e => handleChange('phone', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>العنوان</label>
              <input type="text" value={form.address} onChange={e => handleChange('address', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>الدور *</label>
              <select required value={form.roleId} onChange={e => handleChange('roleId', e.target.value)} className={inputClass}>
                <option value="admin-role-001">مدير نظام (Admin)</option>
                <option value="ceo-role-001">المدير التنفيذي (CEO)</option>
                <option value="delegate-role-001">مندوب (Delegate)</option>
                <option value="merchant-role-001">تاجر (Merchant)</option>
                <option value="staff-role-001">موظف (Staff)</option>
                <option value="support-role-001">الدعم (Support)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>الحالة</label>
              <select value={form.isActive ? 'true' : 'false'} onChange={e => handleChange('isActive', e.target.value === 'true')} className={inputClass}>
                <option value="true">نشط</option>
                <option value="false">غير نشط</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={() => navigate('/users')} className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition">
              إلغاء
            </button>
            <button type="submit" disabled={loading} className="bg-gradient-to-l from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-bold px-8 py-3 rounded-xl shadow-lg disabled:opacity-50 flex items-center gap-2 transition-all">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {loading ? 'جاري الحفظ...' : 'حفظ المستخدم'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
